import { cloneElement, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import {
  STEPS, QUESTION_STEPS, STEP_EVENT, ANSWER_EVENT, PURPOSES, PROPERTY_TYPES, CONFIGS, BUDGETS, POSSESSIONS,
  PRIORITIES, VISITS, nextStep, prevStep, questionProgress, loadState, saveState, sessionId, track, readUtm,
} from "../lib/journey";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { Opening, Intro, Compare } from "../components/journey/IntroScreens";
import { OptionsStep, MultiStep } from "../components/journey/Questions";
import { CityStep, AreaStep } from "../components/journey/CityArea";
import { Searching } from "../components/journey/Searching";
import { ContactForm } from "../components/journey/ContactForm";
import { ThankYou } from "../components/journey/ThankYou";
import { Results } from "../components/journey/Results";
import { WiderSearch } from "../components/journey/WiderSearch";

const toApiAnswers = (a) => ({
  purpose: a.purpose, city: a.city, area: a.area || "", property_type: a.type, configuration: a.config,
  budget: a.budget, possession: a.possession, priorities: a.priorities || [], visit_timing: a.visit,
});

const SEARCH_KEYS = ["purpose", "city", "area", "type", "config", "budget", "possession"];

const firstMissing = (a) => QUESTION_STEPS.find((s) => {
  if (s === "area") return false;
  return s === "priorities" ? !(a.priorities || []).length : !a[s];
});

export default function Journey() {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(() => {
    const s = loadState() || {};
    const prev = s.previous?.[s.previous.length - 1];
    if (!s.lead && prev && !new URLSearchParams(window.location.search).get("s")) {
      Object.assign(s, { lead: prev.lead, match: prev.match, answers: prev.answers, previous: s.previous.slice(0, -1) });
    }
    return { answers: {}, utm: readUtm(), ...s };
  });
  const { data: config } = useQuery({ queryKey: ["public-config"], queryFn: () => api.get("/public/config").then((r) => r.data) });

  const a = state.answers;
  const lead = state.lead;
  const match = state.match;
  const hasResults = match?.status === "matched" && match.tiers?.length > 0;
  let step = STEPS.includes(params.get("s")) ? params.get("s") : lead ? "thanks" : "opening";
  if (step === "thanks" && !lead) step = "opening";
  if (step === "results" && !match) step = "searching";
  if ((step === "searching" || step === "contact") && firstMissing(a)) step = firstMissing(a);
  if (step === "contact" && !match && !firstMissing(a)) step = "searching";
  if (step === "contact" && match && !hasResults && !match.personal) step = "results";
  if (lead && ["searching", "results", "contact"].includes(step)) step = "thanks";

  useEffect(() => saveState(state), [state]);
  useEffect(() => {
    sessionId();
    track(STEP_EVENT[step]);
    window.scrollTo({ top: 0 });
  }, [step]);

  const goto = useCallback((s, replace = false) => setParams({ s }, { replace }), [setParams]);
  const back = () => goto(prevStep(step, a));
  const progress = QUESTION_STEPS.includes(step) ? questionProgress(step, a) : null;

  const saveEdit = async (answers) => {
    await api.put(`/public/leads/${lead.id}`, { answers: toApiAnswers(answers) }, { headers: { "X-Edit-Token": lead.token } });
    setState((s) => ({ ...s, justUpdated: true }));
    goto("thanks");
  };

  const answer = (key, value, extra = {}) => {
    const answers = { ...a, [key]: value, ...extra };
    const staleMatch = SEARCH_KEYS.includes(key) && JSON.stringify(a[key]) !== JSON.stringify(value);
    setState((s) => ({ ...s, answers, justUpdated: false, ...(staleMatch && !s.lead ? { match: undefined } : {}) }));
    track(ANSWER_EVENT[key]);
    let nxt = nextStep(key, answers);
    if (nxt === "searching" && hasResults && !staleMatch && !lead) nxt = "results";
    if (lead && nxt === "searching") return saveEdit(answers).catch(() => goto("thanks"));
    if (key !== "priorities") setTimeout(() => goto(nxt), 200);
    else goto(nxt);
  };

  const pickType = (t) => {
    const extra = {};
    if (!CONFIGS[t].includes(a.config)) extra.config = "";
    if (!BUDGETS[t].includes(a.budget)) extra.budget = "";
    answer("type", t, extra);
  };

  const pickCity = (c) => answer("city", c, c !== a.city ? { area: "" } : {});

  const requested = hasResults ? match.requested || [] : [];
  const cart = match?.tiers?.flatMap((t) => t.projects).filter((p) => requested.includes(p.ref)) || [];

  const runSearch = () =>
    api.post("/public/match/search", { answers: toApiAnswers(a), session_id: sessionId() })
      .then((r) => r.data)
      .catch(() => ({ status: "unavailable" }));

  const searchDone = (result) => {
    setState((s) => ({ ...s, match: { ...result, selected: [], requested: [] } }));
    goto("results", true);
  };

  const requestMatchMaking = (refs) => {
    setState((s) => ({ ...s, match: { ...s.match, selected: refs, requested: refs } }));
    track("matchmaking_requested");
    goto("contact");
  };

  const submitLead = async ({ contact, form_token, website, draft }) => {
    const { data } = await api.post("/public/leads", {
      answers: toApiAnswers(a), contact, form_token, website, session_id: sessionId(), utm: state.utm || {},
      match: requested.length ? { search_id: match.search_id, project_refs: requested } : null,
    });
    track("contact_submitted");
    setState((s) => ({ ...s, previous: [], contactDraft: { ...draft, website: "" }, lead: { id: data.lead_id, token: data.edit_token, name: contact.name, email: contact.email, cart } }));
    goto("thanks", true);
  };

  const startNewSearch = (to) => {
    setState((s) => ({
      ...s, lead: undefined, justUpdated: false, previous: [...(s.previous || []).slice(-4), { lead: s.lead, match: s.match, answers: s.answers }],
      match: to === "results" ? { ...s.match, selected: [], requested: [], already: [...new Set([...(s.match.already || []), ...(s.match.requested || [])])] } : undefined,
    }));
    track(to === "results" ? "relook_matches" : "search_more");
    goto(to);
  };

  const cities = config?.cities || [];
  const cityObj = cities.find((c) => c.name === a.city);
  const q = { onBack: back, progress };

  const screens = {
    opening: <Opening onYes={() => goto("intro")} onExplore={() => goto("intro")} />,
    intro: <Intro onBack={back} onNext={() => goto("compare")} />,
    compare: <Compare onBack={back} onNext={() => goto("purpose")} />,
    purpose: <OptionsStep id="purpose" eyebrow="Let's begin" title="What is the property mainly for?" options={PURPOSES} value={a.purpose} onPick={(v) => answer("purpose", v)} {...q} />,
    city: <CityStep cities={cities} value={a.city} onPick={pickCity} {...q} />,
    area: <AreaStep city={a.city} areas={cityObj?.areas || []} value={a.area} onPick={(v) => answer("area", v)} {...q} />,
    type: <OptionsStep id="type" title="What type of property?" options={PROPERTY_TYPES} value={a.type} onPick={pickType} {...q} />,
    config: <OptionsStep id="config" title={a.type === "Plot" ? "What plot size?" : a.type === "Commercial" ? "Office or retail, and what size?" : a.type === "Apartment" ? "How many bedrooms (BHK)?" : "How many bedrooms and what size?"} options={CONFIGS[a.type] || CONFIGS.Apartment} value={a.config} onPick={(v) => answer("config", v)} {...q} />,
    budget: <OptionsStep id="budget" title="What's your budget in INR?" hint="All-inclusive estimate is fine." options={BUDGETS[a.type] || BUDGETS.Apartment} value={a.budget} onPick={(v) => answer("budget", v)} {...q} />,
    possession: <OptionsStep id="possession" title="When would you like possession?" options={POSSESSIONS} value={a.possession} onPick={(v) => answer("possession", v)} {...q} />,
    priorities: <MultiStep id="priorities" title="What matters most to you?" hint="Choose up to 3." options={PRIORITIES} max={3} value={a.priorities || []} onChange={(v) => setState((s) => ({ ...s, answers: { ...s.answers, priorities: v } }))} onNext={() => answer("priorities", a.priorities)} {...q} />,
    visit: <OptionsStep id="visit" title="When are you next visiting India?" options={VISITS} value={a.visit} onPick={(v) => answer("visit", v)} {...q} />,
    searching: <Searching run={runSearch} onDone={searchDone} />,
    results: !hasResults ? match && (
      <WiderSearch answers={toApiAnswers(a)} onBack={() => goto("visit")} onChange={() => goto("purpose")}
        onRequest={() => { setState((s) => ({ ...s, match: { ...s.match, personal: true } })); track("matchmaking_requested"); goto("contact"); }} />
    ) : (
      <Results match={match} onBack={() => goto("visit")} onNext={requestMatchMaking}
        onChange={(sel) => setState((s) => ({ ...s, match: { ...s.match, selected: sel } }))} />
    ),
    contact: <ContactForm initial={state.contactDraft} onSubmit={submitLead} cartCount={requested.length} matchStatus={match?.status}
      onBack={() => goto(match ? "results" : "visit")} />,
    thanks: lead && (
      <ThankYou name={lead.name} email={lead.email} cart={lead.cart || []} answers={toApiAnswers(a)} conectrUrl={config?.conectr_url} justUpdated={state.justUpdated} onEdit={() => goto("purpose")}
        canRelook={hasResults} onRelook={() => startNewSearch("results")} onSearchMore={() => startNewSearch("purpose")} />
    ),
  };

  const showFooter = ["opening", "intro", "compare", "thanks"].includes(step);

  return (
    <div className="jaali-bg min-h-screen" data-testid="journey-page">
      <Header />
      <main className="relative">
        {lead && QUESTION_STEPS.includes(step) && (
          <p className="mx-auto max-w-xl px-5 pt-3 text-xs font-medium text-[#138808]" data-testid="edit-mode-banner">
            Editing your requirements — changes update your existing enquiry.
          </p>
        )}
        <AnimatePresence mode="wait">{screens[step] && cloneElement(screens[step], { key: step })}</AnimatePresence>
      </main>
      {showFooter && <Footer config={config} />}
    </div>
  );
}
